import React, { useState } from 'react';
import { X, Building2, CheckCircle, AlertCircle } from 'lucide-react';
import { useGst } from '../context/GstContext';
import { EntityType, FilingFrequency } from '../types/gst';
import { validateGstin, GST_STATE_CODES } from '../services/mathValidator';
import { toast } from './ui';

export const GstProfileModal: React.FC = () => {
  const { showProfileModal, setShowProfileModal, saveGstProfile, clientLegalName } = useGst();

  const [entityType, setEntityType] = useState<EntityType>('PVT_LTD');
  const [gstin, setGstin] = useState<string>('27AABCU9603R1ZM');
  const [legalName, setLegalName] = useState<string>(clientLegalName || '');
  const [tradeName, setTradeName] = useState<string>('');
  const [cinOrLlpin, setCinOrLlpin] = useState<string>('U72900MH2024PTC428901');
  const [filingFrequency, setFilingFrequency] = useState<FilingFrequency>('MONTHLY');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  if (!showProfileModal) return null;

  const gstinValidation = validateGstin(gstin);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!gstinValidation.isValid) {
      setError(gstinValidation.reason || 'Invalid GSTIN');
      return;
    }

    try {
      setIsSubmitting(true);
      await saveGstProfile({
        entityType,
        gstin: gstin.toUpperCase(),
        legalName: legalName || clientLegalName,
        tradeName,
        cinOrLlpin,
        filingFrequency
      });
      setShowProfileModal(false);
      toast.success(`GST profile for ${gstin.toUpperCase()} saved successfully`);
    } catch (err: any) {
      setError(err.message || 'Failed to save GST profile');
      toast.error('Failed to save GST profile: ' + (err?.message || 'Unknown error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="relative w-full max-w-lg rounded-2xl border border-[rgba(247,247,248,0.14)] bg-[#131316] p-6 shadow-2xl">
        <button
          onClick={() => setShowProfileModal(false)}
          className="absolute top-4 right-4 text-slate-400 hover:text-white transition"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="flex items-center gap-2.5 mb-4">
          <div className="p-2 rounded-xl bg-cyan-500/10 border border-cyan-500/20 text-cyan-400">
            <Building2 className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">GST Entity Profile Setup</h2>
            <p className="text-xs text-slate-400">Configure compliant corporate entity registration details</p>
          </div>
        </div>

        {error && (
          <div className="mb-4 flex items-center gap-2 rounded-xl bg-rose-500/10 border border-rose-500/20 p-3 text-xs text-rose-300">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 text-xs">
          {/* Entity Type */}
          <div>
            <label className="block font-medium text-slate-300 mb-1">Entity Type</label>
            <div className="grid grid-cols-4 gap-2">
              {(['PVT_LTD', 'LLP', 'OPC', 'SEC_8'] as EntityType[]).map((type) => (
                <button
                  type="button"
                  key={type}
                  onClick={() => setEntityType(type)}
                  className={`py-2 px-1 rounded-xl font-semibold border transition text-center ${
                    entityType === type
                      ? 'bg-cyan-500/20 text-cyan-300 border-cyan-500/50'
                      : 'bg-white/[0.03] text-slate-400 border-white/5 hover:bg-white/[0.06]'
                  }`}
                >
                  {type === 'PVT_LTD' ? 'Pvt Ltd' : type === 'SEC_8' ? 'Section 8' : type}
                </button>
              ))}
            </div>
          </div>

          {/* Primary GSTIN */}
          <div>
            <label className="block font-medium text-slate-300 mb-1">
              Primary GSTIN (15 Characters)
            </label>
            <input
              type="text"
              value={gstin}
              maxLength={15}
              onChange={(e) => setGstin(e.target.value.toUpperCase())}
              placeholder="e.g. 27AABCU9603R1ZM"
              className="w-full rounded-xl border border-white/10 bg-[#0e1015] px-3 py-2 font-mono text-white placeholder-slate-600 focus:border-cyan-500 focus:outline-none uppercase"
              required
            />
            {gstin.length > 0 && (
              <div className="mt-1 flex items-center gap-1.5 text-[11px]">
                {gstinValidation.isValid ? (
                  <span className="flex items-center gap-1 text-emerald-400">
                    <CheckCircle className="h-3.5 w-3.5" />
                    Valid GSTIN ({gstinValidation.stateName} &bull; State {gstinValidation.stateCode})
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-amber-400">
                    <AlertCircle className="h-3.5 w-3.5" />
                    {gstinValidation.reason}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Legal Name & Trade Name */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block font-medium text-slate-300 mb-1">Company Legal Name</label>
              <input
                type="text"
                value={legalName}
                onChange={(e) => setLegalName(e.target.value)}
                placeholder="Verve AI Labs Private Limited"
                className="w-full rounded-xl border border-white/10 bg-[#0e1015] px-3 py-2 text-white placeholder-slate-600 focus:border-cyan-500 focus:outline-none"
                required
              />
            </div>
            <div>
              <label className="block font-medium text-slate-300 mb-1">Trade Name (Optional)</label>
              <input
                type="text"
                value={tradeName}
                onChange={(e) => setTradeName(e.target.value)}
                placeholder="Verve AI"
                className="w-full rounded-xl border border-white/10 bg-[#0e1015] px-3 py-2 text-white placeholder-slate-600 focus:border-cyan-500 focus:outline-none"
              />
            </div>
          </div>

          {/* PAN & CIN */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block font-medium text-slate-300 mb-1">
                Company PAN (Derived)
              </label>
              <input
                type="text"
                value={gstin.length >= 12 ? gstin.substring(2, 12) : ''}
                readOnly
                placeholder="Auto-extracted"
                className="w-full rounded-xl border border-white/10 bg-[#0e1015]/60 px-3 py-2 font-mono text-cyan-300 placeholder-slate-600 focus:outline-none cursor-not-allowed"
              />
            </div>
            <div>
              <label className="block font-medium text-slate-300 mb-1">CIN / LLPIN</label>
              <input
                type="text"
                value={cinOrLlpin}
                onChange={(e) => setCinOrLlpin(e.target.value.toUpperCase())}
                placeholder="U72900MH2024PTC428901"
                className="w-full rounded-xl border border-white/10 bg-[#0e1015] px-3 py-2 font-mono text-white placeholder-slate-600 focus:border-cyan-500 focus:outline-none uppercase"
              />
            </div>
          </div>

          {/* Filing Frequency */}
          <div>
            <label className="block font-medium text-slate-300 mb-1">Filing Frequency</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setFilingFrequency('MONTHLY')}
                className={`py-2 rounded-xl font-semibold border transition text-center ${
                  filingFrequency === 'MONTHLY'
                    ? 'bg-cyan-500/20 text-cyan-300 border-cyan-500/50'
                    : 'bg-white/[0.03] text-slate-400 border-white/5 hover:bg-white/[0.06]'
                }`}
              >
                Monthly (Standard)
              </button>
              <button
                type="button"
                onClick={() => setFilingFrequency('QRMP')}
                className={`py-2 rounded-xl font-semibold border transition text-center ${
                  filingFrequency === 'QRMP'
                    ? 'bg-cyan-500/20 text-cyan-300 border-cyan-500/50'
                    : 'bg-white/[0.03] text-slate-400 border-white/5 hover:bg-white/[0.06]'
                }`}
              >
                QRMP (Quarterly)
              </button>
            </div>
          </div>

          {/* Submit */}
          <div className="pt-2">
            <button
              type="submit"
              disabled={isSubmitting || !gstinValidation.isValid}
              className="w-full rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 py-2.5 font-bold text-black transition hover:opacity-90 disabled:opacity-50"
            >
              {isSubmitting ? 'Saving Profile...' : 'Save GST Entity Profile'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
